/**
 * payoffFromFills — build a payoff-at-expiry curve for a LIVE option position from
 * its actual ENTRY fills (not today's re-entry prices), so a running basket can show
 * the true P&L outcome it's carrying.
 *
 * Each leg's expiry P&L (native premium units) at underlying price S:
 *   SELL: (entryFill − intrinsic(S)) × qty      (a short profits as the option decays)
 *   BUY : (intrinsic(S) − entryFill) × qty
 * summed across legs, then scaled to ₹ by the contract value × FX. The shape is fixed
 * by the strikes; anchoring to entryFill is what makes it the ACTUAL position payoff.
 *
 * Output matches the `basket` shape PayoffChartPro consumes (payoffData.spotRange /
 * .expiry, maxProfit, maxLoss, breakEvens, legs).
 */

const USD_TO_INR = 85;

export interface FillLeg {
  action: 'BUY' | 'SELL';
  optionType: 'CE' | 'PE';
  strike: number;
  entryFill: number | null;
  quantity: number;
}

export interface PayoffCard {
  payoffData: { spotRange: number[]; expiry: number[] };
  maxProfit: number;
  maxLoss: number;
  breakEvens: number[];
  legs: Array<{ action: 'BUY' | 'SELL'; optionType: 'CE' | 'PE'; strike: number }>;
}

const intrinsic = (opt: 'CE' | 'PE', strike: number, s: number) =>
  opt === 'CE' ? Math.max(s - strike, 0) : Math.max(strike - s, 0);

/**
 * @param legs        position legs with entry fills (native premium) + quantity
 * @param contractValue base units per contract (Delta BTC 0.001 / ETH 0.01); ₹ = native × cv × FX
 * @param spot        current underlying (used only to centre the sample window)
 * @param fx          native→INR multiplier (Delta = USD_TO_INR; pass 1 for INR-native)
 */
export function payoffFromFills(
  legs: FillLeg[],
  contractValue: number,
  spot: number | null,
  fx: number = USD_TO_INR,
): PayoffCard | null {
  const usable = (legs || []).filter(l => l.entryFill != null && Number.isFinite(Number(l.entryFill)) && Number(l.quantity) > 0 && Number(l.strike) > 0);
  if (!usable.length || !(contractValue > 0)) return null;

  const strikes = usable.map(l => Number(l.strike));
  const minK = Math.min(...strikes), maxK = Math.max(...strikes);
  const centre = spot && spot > 0 ? spot : (minK + maxK) / 2;
  // Sample window: cover the strikes + spot with generous padding so both tails
  // (where the curve goes flat) are visible; PayoffChartPro re-clamps to the action zone.
  const pad = Math.max(maxK - minK, centre * 0.18, 1);
  const lo = Math.max(0, Math.min(minK, centre) - pad);
  const hi = Math.max(maxK, centre) + pad;
  const N = 161;
  const step = (hi - lo) / (N - 1);

  const inrAt = (s: number): number => {
    let native = 0;
    for (const l of usable) {
      const iv = intrinsic(l.optionType, Number(l.strike), s);
      const per = l.action === 'SELL' ? (Number(l.entryFill) - iv) : (iv - Number(l.entryFill));
      native += per * Number(l.quantity);
    }
    return native * contractValue * fx;
  };

  const spotRange: number[] = [];
  const expiry: number[] = [];
  for (let i = 0; i < N; i++) { const s = lo + i * step; spotRange.push(s); expiry.push(inrAt(s)); }

  let maxProfit = -Infinity, maxLoss = Infinity;
  for (const y of expiry) { if (y > maxProfit) maxProfit = y; if (y < maxLoss) maxLoss = y; }

  // Break-evens: zero-crossings (linear interpolation between samples).
  const breakEvens: number[] = [];
  for (let i = 1; i < expiry.length; i++) {
    const y0 = expiry[i - 1], y1 = expiry[i];
    if ((y0 <= 0 && y1 > 0) || (y0 >= 0 && y1 < 0)) {
      const t = y0 === y1 ? 0 : y0 / (y0 - y1);
      breakEvens.push(spotRange[i - 1] + t * (spotRange[i] - spotRange[i - 1]));
    }
  }

  return {
    payoffData: { spotRange, expiry },
    maxProfit: Math.max(0, maxProfit),
    maxLoss: Math.min(0, maxLoss),
    breakEvens,
    legs: usable.map(l => ({ action: l.action, optionType: l.optionType, strike: Number(l.strike) })),
  };
}
